import { useEffect, useRef, useState } from "react";
/* Solution Imports */
import Feature           from "../models/GISCO/Feature";
import { GiscoResponse } from "../models/GISCO/GiscoResponse";
import giscoClient       from "../services/clients/GiscoClient";

/** Custom properties for GiscoAutocomplete component 
 * can be consumed by the parent element 
 */
type AutocompleteProps = {
    id: string;
    onFeatureChange: (feature: Feature) => void;
}

const GiscoAutocomplete = ({ onFeatureChange, id }: AutocompleteProps) => {

    const [input, setInput]                 = useState<string>("");
    const [selectedIndex, setSelectedIndex] = useState<number>(-1);
    const [features, setFeatures]           = useState<Feature[]>([]);

    const inputHTMLElement = useRef<HTMLInputElement>(null);
    const autocompleteList = useRef(null);

    const allowedFeatureTypes: string[] = ["locality", "street", "city", "house"];

    /**
     * Method that uses the provides user input to check for autocompletion.
     * @param input User input that will be queried against GISCO API.
     * @returns Collection of valid GISCO entries.
     */
    async function fetchGiscoAutoComplete(input: string): Promise<GiscoResponse | null> {

        if (input.length < 3) { return null; } /* Offset | User has to provide at least three characaters as input */

        /* Fetch autocomplete list for user input */
        var response = await giscoClient.get<GiscoResponse>("?q=" + input + "&lang=en");
        if (response.status === 200)
        {
            return response.data;
        }

        return null; // Default return value
    }

    /**
     * Renders a autocomplete dropdown for current input.
     * @param input User input that will be queried against GISCO API.
     */
    async function renderAutoCompleteItems(input: string): Promise<void> {

        var response: GiscoResponse | null = await fetchGiscoAutoComplete(input);
        if (response !== null)
        {
            /* Limit Feature Collection to GISCO entries which fullfill requirments. */
            setFeatures(response.features.filter(x => allowedFeatureTypes.includes(x.properties.type)));
        }
        else {
            setFeatures([]);
        }
    }

    /**
     * Handles MouseClick for autocomplete list entries.
     * @param feature Selected GISCO entry that needs to be returned to the subscriber.
     */
    function handleMouseClick(feature: Feature) {

        if (inputHTMLElement.current) { inputHTMLElement.current.value = feature.properties.name; }

        setFeatures([]);
        setSelectedIndex(-1);

        /* Use callback method to inform subscriber about changes */
        onFeatureChange(feature); 
    }

    /**
     * Handles keydown event for navigation through autcomplete list.
     * @param e Triggered event.
     */
    function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'ArrowDown' && features.length - 1 > selectedIndex) {
            setSelectedIndex(selectedIndex + 1);
        }
        else if (e.key === 'ArrowUp' && selectedIndex > 0) {
            setSelectedIndex(selectedIndex - 1);
        }
        else if (e.key === 'Enter') {
            /* Prevent the form from being submitted */
            e.preventDefault();
            let feature: Feature | null = features[selectedIndex];
            if (feature)
            {
                handleMouseClick(feature);
            }
        }
    }

    /**
     * Handles onFocus event for input element.
     * @param e Triggered event.
     */
    function onFocus(e: React.FocusEvent<HTMLInputElement, Element>) {
        setInput(e.target.value);
    }

    useEffect(() => {
        renderAutoCompleteItems(input);
    }, [input])
    
    return (
        <div className="autocomplete w-100" style={{zIndex: 1}}>
            <input placeholder="Please insert location or address ..." id={id} className="form-control" ref={inputHTMLElement} onChange={e => setInput(e.target.value)} onKeyDown={onKeyDown} onFocus={onFocus} />
            <div className="autocomplete-items" ref={autocompleteList}>
            {
                features.map((x, index) => {
                    return (
                        <div key={index} onClick={(e) => handleMouseClick(x)} className={selectedIndex === index ? "autocomplete-active" : ""}>
                            <input type="hidden" value={index} key={index} />
                                {x.properties.name}, {x.properties.city}, {x.properties.country}
                        </div>
                    )
                })
            }
            </div>
        </div>
    );
}

export default GiscoAutocomplete;